import { useEffect, useRef, useState } from 'react'
import {
  cargarGoogleMaps,
  hayLlaveDeGoogle,
  llaveDeGoogleRechazada,
  alFallarLaLlave,
  ESTILO_MAPA,
} from '@/lib/mapas/google'
import estilos from './MapaPreview.module.css'

type Props = {
  lat: number | null
  lng: number | null
  radioM?: number
  direccion?: string | null
  alto?: string
  zoom?: number
  colorCirculo?: string
  className?: string
}

export function MapaPreview({
  lat,
  lng,
  radioM = 80,
  direccion,
  alto = '190px',
  zoom = 16,
  colorCirculo = '#ffc43f',
  className = '',
}: Props) {
  const contenedor = useRef<HTMLDivElement | null>(null)
  const mapaRef = useRef<google.maps.Map | null>(null)
  const [googleRechazado, setGoogleRechazado] = useState(() => llaveDeGoogleRechazada())

  const hayMapa = hayLlaveDeGoogle()

  // Escuchar si Google rechaza la llave por restricción de referente HTTP (ej. al abrir en celular)
  useEffect(() => {
    return alFallarLaLlave(() => {
      setGoogleRechazado(true)
    })
  }, [])

  useEffect(() => {
    if (!hayMapa || googleRechazado || lat == null || lng == null || !contenedor.current) return

    let vivo = true
    const centro = { lat: Number(lat), lng: Number(lng) }

    void cargarGoogleMaps()
      .then(({ maps }) => {
        if (!vivo || !contenedor.current) return

        const m = new maps.Map(contenedor.current, {
          styles: ESTILO_MAPA,
          center: centro,
          zoom,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
          zoomControl: true,
          gestureHandling: 'cooperative',
        })

        new google.maps.Marker({
          map: m,
          position: centro,
          title: direccion || 'Ubicación de la obra',
        })

        if (radioM > 0) {
          new google.maps.Circle({
            map: m,
            center: centro,
            radius: Number(radioM),
            fillColor: '#7339ce',
            fillOpacity: 0.22,
            strokeColor: colorCirculo,
            strokeWeight: 2,
          })
        }

        mapaRef.current = m
      })
      .catch((err) => {
        console.error('Error cargando mapa preview:', err)
        if (vivo) setGoogleRechazado(true)
      })

    return () => {
      vivo = false
      mapaRef.current = null
    }
  }, [lat, lng, radioM, zoom, hayMapa, direccion, colorCirculo, googleRechazado])

  if (lat == null || lng == null) {
    return null
  }

  // Si Google rechaza la llave por IP de red local (RefererNotAllowedMapError), usamos respaldo OpenStreetMap
  const delta = 0.0028
  const bbox = [lng - delta, lat - delta, lng + delta, lat + delta].join('%2C')
  const osmUrl = `https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${lat}%2C${lng}`

  return (
    <div
      className={`${estilos.contenedor} ${className}`}
      style={{ height: alto }}
    >
      {hayMapa && !googleRechazado ? (
        <div ref={contenedor} className={estilos.mapa} />
      ) : (
        <iframe
          src={osmUrl}
          title={direccion || 'Ubicación de la obra'}
          style={{
            width: '100%',
            height: '100%',
            border: 0,
            display: 'block',
          }}
          loading="lazy"
        />
      )}
    </div>
  )
}
